import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
export function Contact() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success'>('idle');
  const {
    t
  } = useLanguage();
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormState('submitting');
    // Simulate network request
    setTimeout(() => {
      setFormState('success');
    }, 1000);
  };
  return <section id="contact" className="py-24 md:py-32 bg-white" data-id="element-107">
      <div className="container mx-auto px-6 md:px-12" data-id="element-108">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24" data-id="element-109">
          {/* Left Column - Info */}
          <div data-id="element-110">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} data-id="element-111">
              <p className="text-xs font-semibold tracking-[0.3em] text-gold uppercase mb-6" data-id="element-112">
                {t('contact.eyebrow')}
              </p>
              <h2 className="text-3xl md:text-4xl font-heading font-light text-[#0A0A0A] mb-8" data-id="element-113">
                {t('contact.heading')}
              </h2>
              <p className="text-black/70 font-light leading-relaxed mb-12 max-w-md" data-id="element-114">
                {t('contact.intro')}
              </p>

              <div className="space-y-8" data-id="element-115">
                <div className="flex items-start gap-4" data-id="element-116">
                  <Mail className="w-5 h-5 text-gold mt-1" strokeWidth={1.25} data-id="element-117" />
                  <div data-id="element-118">
                    <p className="text-xs tracking-widest uppercase text-black/50 mb-1" data-id="element-119">
                      {t('contact.emailLabel')}
                    </p>
                    <a href="mailto:sales@paolaskinlab.com" className="text-lg font-light hover:text-gold transition-colors" data-id="element-120">
                      sales@paolaskinlab.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-121">
                  <Phone className="w-5 h-5 text-gold mt-1" strokeWidth={1.25} data-id="element-122" />
                  <div data-id="element-123">
                    <p className="text-xs tracking-widest uppercase text-black/50 mb-1" data-id="element-124">
                      {t('contact.phoneLabel')}
                    </p>
                    <a href="tel:7863763985" className="text-lg font-light hover:text-gold transition-colors" data-id="element-125">
                      786-376-3985
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right Column - Form */}
          <div data-id="element-126">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-127">
              {formState === 'success' ? <div className="p-12 border border-gold/30 bg-gold/5 text-center h-full flex flex-col items-center justify-center min-h-[400px]" data-id="element-128">
                  <h3 className="text-2xl font-serif italic text-gold mb-4" data-id="element-129">
                    {t('contact.form.successTitle')}
                  </h3>
                  <p className="text-black/70 font-light" data-id="element-130">
                    {t('contact.form.successBody')}
                  </p>
                  <button onClick={() => setFormState('idle')} className="mt-8 text-xs tracking-widest uppercase text-black border-b border-black pb-1 hover:text-gold hover:border-gold transition-colors" data-id="element-131">
                    {t('contact.form.sendAnother')}
                  </button>
                </div> : <form onSubmit={handleSubmit} className="space-y-8" data-id="element-132">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-133">
                    <div className="space-y-2" data-id="element-134">
                      <label htmlFor="name" className="text-xs tracking-widest uppercase text-black/60" data-id="element-135">
                        {t('contact.form.name')}
                      </label>
                      <input type="text" id="name" required className="w-full bg-transparent border-b border-black/20 py-3 text-[#0A0A0A] focus:outline-none focus:border-gold transition-colors rounded-none" data-id="element-136" />
                    </div>
                    <div className="space-y-2" data-id="element-137">
                      <label htmlFor="email" className="text-xs tracking-widest uppercase text-black/60" data-id="element-138">
                        {t('contact.form.email')}
                      </label>
                      <input type="email" id="email" required className="w-full bg-transparent border-b border-black/20 py-3 text-[#0A0A0A] focus:outline-none focus:border-gold transition-colors rounded-none" data-id="element-139" />
                    </div>
                  </div>

                  <div className="space-y-2" data-id="element-140">
                    <label htmlFor="phone" className="text-xs tracking-widest uppercase text-black/60" data-id="element-141">
                      {t('contact.form.phoneOptional')}
                    </label>
                    <input type="tel" id="phone" className="w-full bg-transparent border-b border-black/20 py-3 text-[#0A0A0A] focus:outline-none focus:border-gold transition-colors rounded-none" data-id="element-142" />
                  </div>

                  <div className="space-y-2" data-id="element-143">
                    <label htmlFor="message" className="text-xs tracking-widest uppercase text-black/60" data-id="element-144">
                      {t('contact.form.message')}
                    </label>
                    <textarea id="message" required rows={4} className="w-full bg-transparent border-b border-black/20 py-3 text-[#0A0A0A] focus:outline-none focus:border-gold transition-colors resize-none rounded-none" data-id="element-145"></textarea>
                  </div>

                  <button type="submit" disabled={formState === 'submitting'} className="w-full px-8 py-4 bg-[#0A0A0A] text-white text-sm tracking-widest uppercase hover:bg-gold transition-colors duration-300 disabled:opacity-70" data-id="element-146">
                    {formState === 'submitting' ? t('contact.form.submitting') : t('contact.form.submit')}
                  </button>
                </form>}
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}